import { useEffect, useLayoutEffect, useRef } from "react";
import { Box } from "@mui/material";
import {
  useTaskAgentState,
  FunctionCall,
} from "components/hooks/useTaskAgentState";
import { ActionDisplay } from "components/tools/actions/ActionDisplay";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";

interface LiveInlineAgentDisplayProps {
  threadRef: string;
  onComplete?: (calls: FunctionCall[]) => void;
}

export default ({ threadRef, onComplete }: LiveInlineAgentDisplayProps) => {
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const stateManager = useTaskAgentState({ accountType: "member" });
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const stateManagerRef = useRef(stateManager);
  stateManagerRef.current = stateManager;

  useEffect(() => {
    stateManager.actions.updateState({ clientOutputVisible: false });
  }, [threadRef]);

  useEffect(() => {
    const channel = `task-agent/${encodeURIComponent(threadRef)}`;
    const handler = (_channel: string, data: unknown) => {
      stateManager.actions.handleWebSocketMessage(_channel, data);
    };
    registerHandler(channel, handler);
    return () => unregisterHandler(channel, handler);
  }, [
    threadRef,
    registerHandler,
    unregisterHandler,
    stateManager.actions.handleWebSocketMessage,
  ]);

  useLayoutEffect(() => {
    if (stateManager.state.hasCompleted || stateManager.state.hasFailed) {
      const sm = stateManagerRef.current;
      const calls: FunctionCall[] = [
        ...sm.computed.unassociatedActions,
        ...sm.computed.planWithActions.flatMap((p) => p.actions),
      ];
      onCompleteRef.current?.(calls);
    }
  }, [stateManager.state.hasCompleted, stateManager.state.hasFailed]);

  const allFunctionCalls: FunctionCall[] = [
    ...stateManager.computed.unassociatedActions,
    ...stateManager.computed.planWithActions.flatMap((p) => p.actions),
  ];

  if (allFunctionCalls.length === 0) return null;

  return (
    <Box sx={{ width: "100%", marginBottom: "6px" }}>
      {allFunctionCalls.map((call) => (
        <ActionDisplay key={call.id} call={call} accountType="member" />
      ))}
    </Box>
  );
};
